import React, { useState } from "react";

import PrivateNotesSection from "./PrivateNotesSection";
import PublicNotesSection from "./PublicNotesSection";

import { useMeetingNotes, useUpdatePrivateMeetingNotes, useUpdatePublicMeetingNotes } from "../../../../api/hooks";
import useAutosave from "../../../../hooks/useAutosave";
import { useNotesData, useNotesState } from "../hooks";

import { resolveDefaultNotesParticipantId } from "../utils";

import { config } from "../config";
import { useMeetingContext } from "../context";

/**
 * Notes section with shared and private notes.
 * Uses AvatarGroup for switching between participants' notes.
 * Includes autosave functionality for the current user's own notes.
 */
const MeetingDetailsNotes = (): React.ReactElement => {
	const { meeting, userId, participantName, isInteractionDisabled, isAdminView, workspace, participantNames } =
		useMeetingContext();
	const { data: notes, isLoading: isNotesLoading } = useMeetingNotes(meeting._id);

	const { mutate: updatePublicMeetingNotes, isError: isPublicNotesError } = useUpdatePublicMeetingNotes();
	const { mutate: updatePrivateMeetingNotes, isError: isPrivateNotesError } = useUpdatePrivateMeetingNotes();

	const notesData = useNotesData({
		notes,
		userId,
		meetingCreatorId: meeting?.created?.userId,
		meetingParticipants: meeting?.participants,
		participantName,
		isAdminView,
		workspace,
	});

	const {
		publicNoteContent,
		setPublicNoteContent,
		privateNoteContent,
		setPrivateNoteContent,
		isPrivateNotesVisible,
		setIsPrivateNotesVisible,
	} = useNotesState({
		notesData,
		isNotesLoading,
	});

	const meetingCreatorId = meeting?.created?.userId || userId;
	const isCurrentUserParticipant = meeting?.participants?.some((p) => p.userId === userId) ?? false;
	const [selectedParticipantId, setSelectedParticipantId] = useState(() =>
		resolveDefaultNotesParticipantId({
			userId,
			meetingCreatorId,
			isAdminView: isAdminView ?? false,
			participants: meeting?.participants ?? [],
			workspace,
			participantNames,
		})
	);

	const isNotesDisabled = isInteractionDisabled || isNotesLoading;
	const hasPrivateNotes = Boolean(notesData.userPrivateNotes?.content?.trim());
	const shouldShowPrivateNotes = !isAdminView || (isAdminView && isCurrentUserParticipant);

	const {
		showSaved: showPublicNotesSaved,
		showSaving: showPublicNotesSaving,
		handleChange: handlePublicNoteChange,
	} = useAutosave({
		value: publicNoteContent,
		setValue: setPublicNoteContent,
		onSave: async (content) => {
			if (notesData.primaryPublicNotes?._id) {
				updatePublicMeetingNotes({
					noteId: notesData.primaryPublicNotes._id,
					data: { content },
					meetingId: meeting._id,
				});
			}
		},
		delay: config.autosaveDelay,
		enabled: Boolean(notesData.primaryPublicNotes?._id),
		minSavingTime: config.minSavingTime,
	});

	const {
		showSaved: showPrivateNotesSaved,
		showSaving: showPrivateNotesSaving,
		handleChange: handlePrivateNoteChange,
	} = useAutosave({
		value: privateNoteContent,
		setValue: setPrivateNoteContent,
		onSave: async (content) => {
			if (notesData.userPrivateNotes?._id) {
				updatePrivateMeetingNotes({
					noteId: notesData.userPrivateNotes._id,
					data: { content },
					meetingId: meeting._id,
				});
			}
		},
		delay: config.autosaveDelay,
		enabled: Boolean(notesData.userPrivateNotes?._id) && isPrivateNotesVisible,
		minSavingTime: config.minSavingTime,
	});

	const handleTogglePrivateNotesVisibility = (): void => {
		setIsPrivateNotesVisible(!isPrivateNotesVisible);
	};

	return (
		<div className="flex flex-col gap-4">
			<PublicNotesSection
				notesData={notesData}
				isNotesLoading={isNotesLoading}
				isNotesDisabled={isNotesDisabled}
				selectedParticipantId={selectedParticipantId}
				publicNoteContent={publicNoteContent}
				showPublicNotesSaved={showPublicNotesSaved}
				showPublicNotesSaving={showPublicNotesSaving}
				isPublicNotesError={isPublicNotesError}
				onParticipantSelect={setSelectedParticipantId}
				onPublicNoteChange={handlePublicNoteChange}
			/>
			{shouldShowPrivateNotes && (
				<PrivateNotesSection
					isPrivateNotesVisible={isPrivateNotesVisible}
					hasPrivateNotes={hasPrivateNotes}
					isNotesLoading={isNotesLoading}
					isInteractionDisabled={isInteractionDisabled || false}
					isNotesDisabled={isNotesDisabled}
					privateNoteContent={privateNoteContent}
					showPrivateNotesSaved={showPrivateNotesSaved}
					showPrivateNotesSaving={showPrivateNotesSaving}
					isPrivateNotesError={isPrivateNotesError}
					onPrivateNoteChange={handlePrivateNoteChange}
					onToggleVisibility={handleTogglePrivateNotesVisibility}
				/>
			)}
		</div>
	);
};

export default MeetingDetailsNotes;
